import React, { useState, useMemo } from 'react';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json';
import { useTranslation } from 'hooks';
import { resendPaymentCallback } from 'api/payment';
import Button from 'components/ui/button';
import CopyToClipboard from 'components/ui/copyToClipboard';
import './callbackInfo.scss';

SyntaxHighlighter.registerLanguage('json', json);

interface Props {
  callback: {
    callbackBody: string;
    callbackAttempts: string;
    callbackCreationDate: string;
    callbackLastAttempt: string;
    callbackStatus: string;
  };
  transactionId: string;
  operationId: string;
  resendEnabled: boolean;
}

export const CallbackInfo: React.FC<Props> = ({
  callback,
  transactionId,
  operationId,
  resendEnabled,
}) => {
  const getTranslate = useTranslation();
  const [resending, setResending] = useState(false);
  const {
    callbackBody,
    callbackAttempts,
    callbackCreationDate,
    callbackLastAttempt,
    callbackStatus,
  } = callback;

  const parsedData = useMemo(() => {
    try {
      return JSON.stringify(JSON.parse(callbackBody), undefined, 4);
    } catch (err) {
      console.error(err);
      return '';
    }
  }, [callbackBody]);

  const resend = async () => {
    setResending(true);

    try {
      await resendPaymentCallback(transactionId, operationId);
    } catch (err) {
      console.error(err);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className='callback-info flex h-full flex-col'>
      <div className='callback-info__meta max-tablet:flex-col max-tablet:gap-3 border-border-base flex overflow-auto border-b p-5'>
        <div className='callback-info__meta-item border-border-base max-tablet:p-0 max-tablet:flex-row max-tablet:justify-between max-tablet:border-none relative flex flex-col gap-2 border-r px-5 first:pl-0 last:border-none last:pr-0'>
          <h4 className='callback-info__meta-label text-m-regular text-text-secondary whitespace-nowrap'>
            {getTranslate('payments.data.callbackstatus')}
          </h4>
          <p className='callback-info__meta-value text-m-regular whitespace-nowrap'>{callbackStatus}</p>
        </div>
        <div className='callback-info__meta-item border-border-base max-tablet:p-0 max-tablet:flex-row max-tablet:justify-between max-tablet:border-none relative flex flex-col gap-2 border-r px-5 first:pl-0 last:border-none last:pr-0'>
          <h4 className='callback-info__meta-label text-m-regular text-text-secondary whitespace-nowrap'>
            {getTranslate('payments.data.attempts')}
          </h4>
          <p className='callback-info__meta-value text-m-regular whitespace-nowrap'>{callbackAttempts}</p>
        </div>
        <div className='callback-info__meta-item border-border-base max-tablet:p-0 max-tablet:flex-row max-tablet:justify-between max-tablet:border-none relative flex flex-col gap-2 border-r px-5 first:pl-0 last:border-none last:pr-0'>
          <h4 className='callback-info__meta-label text-m-regular text-text-secondary whitespace-nowrap'>
            {getTranslate('payments.data.creationtime')}
          </h4>
          <p className='callback-info__meta-value text-m-regular whitespace-nowrap'>
            {callbackCreationDate}
          </p>
        </div>
        <div className='callback-info__meta-item border-border-base max-tablet:p-0 max-tablet:flex-row max-tablet:justify-between max-tablet:border-none relative flex flex-col gap-2 border-r px-5 first:pl-0 last:border-none last:pr-0'>
          <h4 className='callback-info__meta-label text-m-regular text-text-secondary whitespace-nowrap'>
            {getTranslate('payments.data.lastattempts')}
          </h4>
          <p className='callback-info__meta-value text-m-regular whitespace-nowrap'>
            {callbackLastAttempt}
          </p>
        </div>
      </div>

      <div className='callback-info__body relative min-h-0 flex-1'>
        <div className='callback-info__actions bg-background-primary max-tablet:static max-tablet:py-4 max-tablet:px-5 absolute top-[20px] right-[20px] flex gap-2'>
          <CopyToClipboard text={parsedData}>
            <Button
              size='normal'
              status='outline'
              text={getTranslate('payments.single.callback.copy.button')}
            />
          </CopyToClipboard>

          {resendEnabled ? (
            <Button
              size='normal'
              status='primary'
              text={getTranslate(
                'payments.single.callback.resendCallback.button'
              )}
              onClick={resend}
              loading={resending}
            />
          ) : null}
        </div>

        <SyntaxHighlighter showLineNumbers wrapLines useInlineStyles={false} className='callback-info__code'>
          {parsedData}
        </SyntaxHighlighter>
      </div>
    </div>
  );
};
